import { create } from "zustand";
import type { LogoutOptions, RedirectLoginOptions } from "@auth0/auth0-react";
import type { JwtClaims } from "./claims";

export type SessionState =
  | {
      status: "loading";
      accessToken: null;
      claims: null;
      tenantIds: string[];
      roles: string[];
      permissions: string[];
      error: null;
    }
  | {
      status: "signed_out";
      accessToken: null;
      claims: null;
      tenantIds: string[];
      roles: string[];
      permissions: string[];
      error: null;
    }
  | {
      status: "authenticated";
      accessToken: string;
      claims: JwtClaims;
      tenantIds: string[];
      roles: string[];
      permissions: string[];
      error: null;
    }
  | {
      status: "error";
      accessToken: null;
      claims: null;
      tenantIds: string[];
      roles: string[];
      permissions: string[];
      error: string;
    };

interface AuthStore {
  sessionState: SessionState;
  getAccessTokenSilently: (() => Promise<string>) | null;
  loginWithRedirect: ((options?: RedirectLoginOptions) => Promise<void>) | null;
  logout: ((options?: LogoutOptions) => Promise<void>) | null;
  setSessionState: (state: SessionState) => void;
  setGetAccessTokenSilently: (fn: () => Promise<string>) => void;
  setLoginWithRedirect: (
    fn: (options?: RedirectLoginOptions) => Promise<void>,
  ) => void;
  setLogout: (fn: (options?: LogoutOptions) => Promise<void>) => void;
}

export const useAuthStore = create<AuthStore>((set) => ({
  sessionState: {
    status: "loading",
    accessToken: null,
    claims: null,
    tenantIds: [],
    roles: [],
    permissions: [],
    error: null,
  },
  getAccessTokenSilently: null,
  loginWithRedirect: null,
  logout: null,
  setSessionState: (state) => set({ sessionState: state }),
  setGetAccessTokenSilently: (fn) => set({ getAccessTokenSilently: fn }),
  setLoginWithRedirect: (fn) => set({ loginWithRedirect: fn }),
  setLogout: (fn) => set({ logout: fn }),
}));

export function useClaritySession() {
  return useAuthStore((state) => state.sessionState);
}
